import type { MemAgentSlackConnectSource } from "@/domains/metrics/tracked-event";
import {
  SubscriptionPlanKind,
  type SubscriptionPlanKind as SubscriptionPlanKindType,
} from "@/store/subscriptions/types";

const SLACK_OAUTH_STATE_KEY = "mem.slack.oauth_state";

export type SlackOAuthConnectionIntent = "connect" | "reauthorize";

export interface SlackOAuthContext {
  state: string;
  source: MemAgentSlackConnectSource;
  is_setup: boolean;
  connection_intent: SlackOAuthConnectionIntent;
  original_plan_kind?: SubscriptionPlanKindType;
  original_effective_plan_kind?: SubscriptionPlanKindType;
}

const DEFAULT_SLACK_OAUTH_CONTEXT: Pick<
  SlackOAuthContext,
  "source" | "is_setup" | "connection_intent"
> = {
  source: "settings",
  is_setup: false,
  connection_intent: "connect",
};

const isMemAgentSlackConnectSource = (source: unknown): source is MemAgentSlackConnectSource => {
  return source === "settings" || source === "proactive_checkout";
};

const isSlackOAuthConnectionIntent = (intent: unknown): intent is SlackOAuthConnectionIntent => {
  return intent === "connect" || intent === "reauthorize";
};

const parseSubscriptionPlanKind = (value: unknown): SubscriptionPlanKindType | undefined => {
  if (typeof value !== "string") {
    return undefined;
  }

  return (Object.values(SubscriptionPlanKind) as string[]).includes(value)
    ? (value as SubscriptionPlanKindType)
    : undefined;
};

const parseSlackOAuthContext = (rawValue: string): SlackOAuthContext => {
  try {
    const parsed = JSON.parse(rawValue) as {
      state?: unknown;
      source?: unknown;
      is_setup?: unknown;
      isSetup?: unknown;
      connection_intent?: unknown;
      original_plan_kind?: unknown;
      original_effective_plan_kind?: unknown;
    };

    if (typeof parsed.state !== "string" || parsed.state.length === 0) {
      throw new Error("Missing Slack OAuth state");
    }

    return {
      state: parsed.state,
      source: isMemAgentSlackConnectSource(parsed.source)
        ? parsed.source
        : DEFAULT_SLACK_OAUTH_CONTEXT.source,
      is_setup:
        typeof parsed.is_setup === "boolean"
          ? parsed.is_setup
          : typeof parsed.isSetup === "boolean"
            ? parsed.isSetup
            : DEFAULT_SLACK_OAUTH_CONTEXT.is_setup,
      connection_intent: isSlackOAuthConnectionIntent(parsed.connection_intent)
        ? parsed.connection_intent
        : DEFAULT_SLACK_OAUTH_CONTEXT.connection_intent,
      original_plan_kind: parseSubscriptionPlanKind(parsed.original_plan_kind),
      original_effective_plan_kind: parseSubscriptionPlanKind(parsed.original_effective_plan_kind),
    };
  } catch {
    return {
      state: rawValue,
      ...DEFAULT_SLACK_OAUTH_CONTEXT,
    };
  }
};

export const writeSlackOAuthContext = (context: SlackOAuthContext) => {
  window.localStorage.setItem(SLACK_OAUTH_STATE_KEY, JSON.stringify(context));
};

export const writeSlackOAuthState = (state: string) => {
  writeSlackOAuthContext({
    state,
    ...DEFAULT_SLACK_OAUTH_CONTEXT,
  });
};

export const readSlackOAuthContext = (): SlackOAuthContext | null => {
  const rawValue = window.localStorage.getItem(SLACK_OAUTH_STATE_KEY);
  if (!rawValue) {
    return null;
  }

  return parseSlackOAuthContext(rawValue);
};

export const readSlackOAuthState = (): string | null => {
  return readSlackOAuthContext()?.state ?? null;
};

export const clearSlackOAuthState = () => {
  window.localStorage.removeItem(SLACK_OAUTH_STATE_KEY);
};
